<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import whatsappCallingClient from 'dashboard/helper/whatsappCallingClient';
import { useCallsStore } from 'dashboard/stores/calls';

const props = defineProps({
  chat: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const callsStore = useCallsStore();
const { t } = useI18n();

const isCalling = ref(false);
const isRequestingPermission = ref(false);
const isCheckingPermission = ref(true);
const permissionStatus = ref('unknown'); // granted, pending, denied, expired, none, unknown

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const phone = computed(() => currentContact.value?.phone_number);

const canCallFlag = ref(false);
const canCall = computed(() => canCallFlag.value);
const canRequestPermission = computed(
  () =>
    !!phone.value &&
    !canCall.value &&
    !isCheckingPermission.value &&
    ['none', 'expired', 'denied', 'unknown'].includes(permissionStatus.value)
);
const isPending = computed(() => permissionStatus.value === 'pending');

const checkPermission = async () => {
  if (!phone.value) {
    isCheckingPermission.value = false;
    return;
  }

  isCheckingPermission.value = true;
  try {
    const { data } = await InboxesAPI.checkCallPermission(
      props.chat.inbox_id,
      phone.value
    );
    permissionStatus.value = data.status || 'none';
    canCallFlag.value = !!data.can_call;
  } catch {
    // If check fails, fall back to contact custom_attributes
    const hasLocalPermission =
      !!currentContact.value?.custom_attributes?.wa_call_permission_granted;
    permissionStatus.value = hasLocalPermission ? 'granted' : 'unknown';
    canCallFlag.value = hasLocalPermission;
  } finally {
    isCheckingPermission.value = false;
  }
};

onMounted(() => {
  checkPermission();
});

// Re-check when contact changes (e.g., navigating between conversations)
watch(
  () => props.chat.meta.sender.id,
  () => {
    permissionStatus.value = 'unknown';
    checkPermission();
  }
);

const requestPermission = async () => {
  isRequestingPermission.value = true;
  try {
    await InboxesAPI.requestCallPermission(props.chat.inbox_id, {
      to: phone.value,
      conversation_id: props.chat.id,
      body_text:
        t('CONVERSATION.WHATSAPP_CALLING.PERMISSION_REQUEST_BODY') ||
        'We would like to call you on WhatsApp to continue this conversation.',
    });
    useAlert(
      t('CONVERSATION.WHATSAPP_CALLING.PERMISSION_REQUESTED') ||
        'Permission request sent.'
    );
    permissionStatus.value = 'pending';
  } catch (error) {
    const apiError = error?.response?.data?.error || error.message;
    useAlert(apiError || 'Failed to request permission.');
  } finally {
    isRequestingPermission.value = false;
  }
};

const initiateCall = async () => {
  if (!canCall.value) {
    useAlert(
      t('CONVERSATION.WHATSAPP_CALLING.ERRORS.NO_PERMISSION') ||
        'Call permission not granted by user.'
    );
    return;
  }

  isCalling.value = true;
  try {
    const response = await whatsappCallingClient.initiateOutboundCall({
      contactId: currentContact.value.id,
      inboxId: props.chat.inbox_id,
      to: phone.value,
      bizOpaqueCallbackData: props.chat.id,
    });
    const callId = response?.call_session?.call_id;
    if (callId) {
      callsStore.addCall({
        callSid: callId,
        provider: 'whatsapp',
        conversationId: props.chat.id,
        inboxId: props.chat.inbox_id,
        callDirection: 'outbound',
        status: 'ringing',
        isActive: true,
      });
    }
    useAlert(
      t('CONVERSATION.WHATSAPP_CALLING.SUCCESS') ||
        'Call initiated successfully.'
    );
  } catch (error) {
    const apiError = error?.response?.data?.error || error.message;
    useAlert(
      apiError ||
        t('CONVERSATION.WHATSAPP_CALLING.ERRORS.FAILED') ||
        'Failed to initiate call.'
    );
  } finally {
    isCalling.value = false;
  }
};
</script>

<template>
  <div class="flex items-center">
    <!-- Loading state while checking permission -->
    <NextButton
      v-if="isCheckingPermission && phone"
      icon="i-lucide-loader-2"
      :title="
        t('CONVERSATION.WHATSAPP_CALLING.CHECKING_PERMISSION') || 'Checking...'
      "
      class="ltr:ml-2 rtl:mr-2 animate-spin"
      color="slate"
      variant="ghost"
      disabled
    />

    <!-- Permission pending -->
    <NextButton
      v-else-if="isPending"
      icon="i-lucide-clock"
      :title="
        t('CONVERSATION.WHATSAPP_CALLING.PERMISSION_PENDING') || 'Pending...'
      "
      class="ltr:ml-2 rtl:mr-2"
      color="slate"
      variant="ghost"
      disabled
    />

    <!-- Request permission (no permission yet) -->
    <NextButton
      v-else-if="canRequestPermission"
      icon="i-lucide-shield-question"
      :title="
        t('CONVERSATION.WHATSAPP_CALLING.REQUEST_PERMISSION') ||
        'Request Call Permission'
      "
      :is-loading="isRequestingPermission"
      class="ltr:ml-2 rtl:mr-2"
      color="slate"
      variant="ghost"
      @click="requestPermission"
    />

    <!-- Call button (permission granted) -->
    <NextButton
      v-if="canCall"
      icon="i-lucide-phone"
      :title="t('CONVERSATION.WHATSAPP_CALLING.BUTTON') || 'Call'"
      :is-loading="isCalling"
      class="ltr:ml-2 rtl:mr-2"
      @click="initiateCall"
    />
  </div>
</template>
